// AndroidSyncCoordinator
// Bridges Capacitor Android lifecycle + network events to existing JS services
// Phase 1: foreground/resumed syncing without native WorkManager

import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Network } from '@capacitor/network';
import connectivityMonitor from './connectivityMonitor';
import syncService from './syncService';

class AndroidSyncCoordinator {
  constructor() {
    this.installed = false;
    this.lastTriggerTs = 0;
    this.minTriggerIntervalMs = 4 * 60 * 1000; // 4 minutes throttle to respect ~5 min cadence
    this.removeListeners = [];
  }

  isAndroidNative() {
    try {
      return Capacitor?.isNativePlatform?.() === true && (Capacitor?.getPlatform?.() === 'android');
    } catch (e) {
      return false;
    }
  }

  async install() {
    if (this.installed || !this.isAndroidNative()) return;
    this.installed = true;

    // Seed connectivity state from Capacitor Network
    try {
      const status = await Network.getStatus();
      connectivityMonitor.setOnline(!!status?.connected);
    } catch (e) {
      // ignore
    }

    // Listen for network status changes: update connectivity only (no immediate sync)
    const netRemove = await Network.addListener('networkStatusChange', (status) => {
      const online = !!status?.connected;
      connectivityMonitor.setOnline(online);
      // Do NOT trigger immediate sync on online event (per app policy)
    });
    this.removeListeners.push(() => netRemove.remove?.());

    // On app resume, if online and enough time since last trigger, run a sync
    const resumeRemove = await CapApp.addListener('resume', async () => {
      const online = connectivityMonitor.getStatus();
      const now = Date.now();
      if (!online) return;
      if (now - this.lastTriggerTs < this.minTriggerIntervalMs) return;

      try {
        // Single-flight is handled inside syncService.triggerSync()
        const result = await syncService.triggerSync();
        if (!result?.skipped && !result?.offline) {
          this.lastTriggerTs = now;
        }
      } catch (e) {
        // swallow; backoff is handled by syncService
        // keep lastTriggerTs unchanged so we may retry on next resume
      }
    });
    this.removeListeners.push(() => resumeRemove.remove?.());

    if (process.env.NODE_ENV !== 'production') {
      console.log('[AndroidSyncCoordinator] Installed listeners for Android resume + network');
    }
  }

  uninstall() {
    this.removeListeners.forEach((fn) => {
      try { fn && fn(); } catch (_) {}
    });
    this.removeListeners = [];
    this.installed = false;
  }
}

export default new AndroidSyncCoordinator();
